import { createSelector } from 'reselect';
import { getTestVariant } from 'utilities';
import { getColor, getRedirectUrl } from 'utilities/global';
import { REGIONS } from 'consts';

const getGlobalState = state => state.global;

export const getLoadingSelector = createSelector([getGlobalState], state => state.loader);

export const getConfigData = createSelector([getGlobalState], state => state.configData);

export const getMerchantData = createSelector([getGlobalState], state => state.merchant);

export const getPreviewConfig = createSelector(
  [getGlobalState],
  state => state.previewConfig
);

export const getPreviewLanguage = createSelector(
  [getPreviewConfig],
  previewConfig => previewConfig?.language || ''
);

export const getMerchantColor = createSelector(
  [getPreviewConfig, getMerchantData],
  (previewConfig, merchant) => getColor(previewConfig.color || merchant.color)
);

export const getMerchantWebsite = createSelector(
  [getMerchantData],
  merchant => merchant.website
);

export const getMerchantCompleteUrl = createSelector(
  [getMerchantData],
  merchant => merchant.completeUrl
);

export const getMerchantCancelUrl = createSelector(
  [getMerchantData],
  merchant => merchant.cancelUrl
);

export const getCancelRedirectUrl = createSelector(
  [getMerchantWebsite, getMerchantCancelUrl],
  (merchantWebsite, cancelUrl) => getRedirectUrl({ url: cancelUrl, merchantWebsite })
);

export const getCompleteRedirectUrl = createSelector(
  [getMerchantWebsite, getMerchantCompleteUrl],
  (merchantWebsite, completeUrl) => getRedirectUrl({ url: completeUrl, merchantWebsite })
);

export const getMerchantLogo = createSelector(
  [getPreviewConfig, getMerchantData],
  (previewConfig, merchant) => previewConfig.logo || merchant.logo
);

export const getMerchantLogoBase64 = createSelector(
  [getMerchantData],
  merchant => merchant.logo_base64
);

export const getMerchantAlias = createSelector(
  [getPreviewConfig, getMerchantData],
  (previewConfig, merchant) => {
    let result;
    if (merchant.alias !== 'N/A') {
      result = merchant.alias;
    }
    return previewConfig.alias || result;
  }
);

export const getMerchantCustomerSupport = createSelector(
  [getMerchantData],
  merchant => merchant.customerSupport || {}
);

export const getMerchantMainButtonText = createSelector(
  [getPreviewConfig, getMerchantData, getConfigData],
  (previewConfig, merchant, config) =>
    previewConfig.ctaText || merchant.mainButton || config.default_checkout_cta
);

const getGeoData = createSelector([getGlobalState], state => state.geo);

export const getRegion = createSelector([getGeoData], state =>
  REGIONS[state.region] ? state.region : REGIONS.AMERICAS
);
export const getGeoCountry = createSelector([getGeoData], state => state.geoCountry);

export const getSdkParams = createSelector([getGlobalState], state => state.sdkParams);

export const getCustomStyles = createSelector(
  [getGlobalState],
  state => state.customStyles
);

export const getParentDomain = createSelector([getSdkParams], state => state.hostname);

export const isSdkSelector = createSelector([getGlobalState], state => state.isSdk);

export const isEmbeddedView = createSelector([getGlobalState], state => state.embedded);

export const isMobileSelector = createSelector([getGlobalState], state => state.isMobile);

export const isMobileNotSdkSelector = createSelector(
  [isSdkSelector, isMobileSelector],
  (isSdk, isMobile) => isMobile && !isSdk
);

export const isPreviewSelector = createSelector(
  [getGlobalState],
  state => state.isPreview
);

export const isDemoSelector = createSelector([getGlobalState], state => state.isDemo);

export const modalSelector = createSelector([getGlobalState], state => state.modal);

export const modalTypeSelector = createSelector([modalSelector], modal => modal.type);

export const modalPropsSelector = createSelector([modalSelector], modal => modal.props);

const paymentAlertData = createSelector([getGlobalState], state => state.paymentAlert);

export const isDisplayPaymentAlert = createSelector(
  [paymentAlertData],
  state => state.shown
);

export const getABTestingData = createSelector(
  [getGlobalState],
  state => state.ABTesting
);

export const getABTestVariant = ({ testName }) =>
  createSelector(
    [getABTestingData, getPageToken, isPreviewSelector],
    (abTestingVariants, pageToken, isPreview) => {
      if (isPreview) return;
      return getTestVariant({ testName, pageToken, abTestingVariants });
    }
  );

export const getPageToken = createSelector([getGlobalState], state => state.pageToken);

export const getFeatureFlags = createSelector(
  [getGlobalState],
  state => state.featureFlags
);

export const getPageType = createSelector([getGlobalState], state => state.pageType);

export const getUserFirstPageUrl = createSelector(
  [getGlobalState],
  state => state.userFirstPageUrl
);

export const bannerSelector = createSelector([getGlobalState], state => state.banner);

export const bannerPropsSelector = createSelector(
  [bannerSelector],
  banner => banner.props
);

export const getOrgId = createSelector([getMerchantData], merchant => merchant.orgId);

export const isNotSdkOrMobileSelector = createSelector(
  [isSdkSelector, isMobileSelector],
  (isSdk, isMobile) => !isSdk && !isMobile
);

export const getDemoCurrentEnv = createSelector(
  [getConfigData],
  state => state.demo_current_env
);

export const getRapydPrivacyPolicyLink = createSelector(
  [getConfigData],
  state => state.rapyd_privacy_policy_link
);
